import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { blogPosts } from '../../mocks/blog';

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [relatedRef, relatedInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  if (!post) {
    return (
      <div className="min-h-screen bg-cream-light flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl font-bold text-navy mb-4">Yazı Bulunamadı</h1>
          <Link to="/blog" className="font-sans text-gold hover:text-gold-light cursor-pointer">
            Blog'a Dön
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-cream-light">
      <Header />

      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        initial={{ opacity: 0 }}
        animate={heroInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.8 }}
        className="relative h-[60vh] flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-sans text-xs text-white uppercase tracking-widest mb-4 block"
          >
            {post.category}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="font-serif text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          >
            {post.title}
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="font-sans text-sm text-gray-300"
          >
            Fatih Sinan Yılmaz • {post.date} • {post.readTime} okuma
          </motion.div>
        </div>
      </motion.section>

      {/* Content Section */}
      <motion.article
        ref={contentRef}
        initial={{ opacity: 0, y: 50 }}
        animate={contentInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className="py-16 lg:py-24"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="prose prose-lg max-w-none">
            {post.content.split('\n\n').map((paragraph, index) => {
              if (paragraph.startsWith('## ')) {
                return (
                  <h2 key={index} className="font-serif text-3xl lg:text-4xl font-bold text-navy mt-12 mb-6">
                    {paragraph.replace('## ', '')}
                  </h2>
                );
              } else if (paragraph.startsWith('### ')) {
                return (
                  <h3 key={index} className="font-serif text-2xl lg:text-3xl font-semibold text-navy mt-9 mb-5">
                    {paragraph.replace('### ', '')}
                  </h3>
                );
              } else if (paragraph.startsWith('> ')) {
                return (
                  <blockquote key={index} className="border-l-4 border-gold bg-cream pl-8 pr-8 py-7 my-8 italic text-gray-700 text-xl leading-relaxed">
                    {paragraph.replace('> ', '').replace(/"/g, '')}
                  </blockquote>
                );
              } else if (paragraph.match(/^\d+\./)) {
                const items = paragraph.split('\n');
                return (
                  <ol key={index} className="list-decimal list-inside space-y-3 my-6 text-gray-700 text-lg leading-relaxed">
                    {items.map((item, i) => (
                      <li key={i}>{item.replace(/^\d+\.\s/, '')}</li>
                    ))}
                  </ol>
                );
              } else {
                return (
                  <p key={index} className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                );
              }
            })}
          </div>

          {/* Social Share */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <h4 className="font-sans text-base font-semibold text-navy mb-4">Paylaş:</h4>
            <div className="flex gap-4">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full hover:bg-gold hover:text-white transition-colors duration-300 cursor-pointer"
                aria-label="Twitter'da Paylaş"
              >
                <i className="ri-twitter-x-line text-lg"></i>
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full hover:bg-gold hover:text-white transition-colors duration-300 cursor-pointer"
                aria-label="LinkedIn'de Paylaş"
              >
                <i className="ri-linkedin-line text-lg"></i>
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full hover:bg-gold hover:text-white transition-colors duration-300 cursor-pointer"
                aria-label="Facebook'ta Paylaş"
              >
                <i className="ri-facebook-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </motion.article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <motion.section
          ref={relatedRef}
          initial={{ opacity: 0, y: 50 }}
          animate={relatedInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="bg-white py-16 lg:py-24"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="mb-12">
              <h3 className="font-serif text-3xl lg:text-4xl font-bold text-navy mb-2">
                İlgili Yazılar
              </h3>
              <div className="w-full h-px bg-gray-200 mt-4"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost, index) => (
                <motion.div
                  key={relatedPost.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={relatedInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link
                    to={`/blog/${relatedPost.slug}`}
                    className="block bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedPost.coverImage}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <span className="font-sans text-xs text-gold uppercase tracking-wider">
                        {relatedPost.category}
                      </span>
                      <h4 className="font-serif text-lg font-bold text-navy mt-2 mb-2 line-clamp-2 leading-snug">
                        {relatedPost.title}
                      </h4>
                      <p className="font-sans text-sm text-gray-500">{relatedPost.date}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
      )}

      <Footer />
    </div>
  );
};

export default BlogDetailPage;